// Google Analytics 4 for the legacy portal. Google only ever sees scrubbed
// pages and pseudonymous ids (see docs/analytics-ga4.md):
// - page_location: origin + the matched ROUTE PATTERN from React Router (e.g.
//   /app/emails/:emailId/edit), never the URL's own path, so only literals from
//   our route table and :param placeholders can reach Google; no match, a
//   splat, or no router yet gives /:unmatched. Then only the five standard
//   utm_* params, with values that can't look personal; never the fragment;
// - page_referrer: '' for either portal, origin only for external sites;
// - page_title: a fixed title, because pages may put names in document.title;
// - user_id / agency_hash: HMACs from GET /api/analytics/identity.
// gtag config turns off Google signals and ad personalization, and scopes the
// _ga cookie to this host so it isn't shared across *.travelminds.com.
// Without a measurement id (local dev) nothing loads and every call is a no-op.
// Dependency-free so tests can run it under node with a mocked gtag.

type Gtag = (...args: unknown[]) => void;

export interface GaIdentity {
  user_id: string;
  user_role: 'admin' | 'owner' | 'staff';
  agency_hash: string | null;
}

export const PAGE_TITLE = 'Marketing Portal';
export const UNMATCHED = '/:unmatched';

const MEASUREMENT_ID = /^G-[A-Z0-9]{4,20}$/;
// What our route table's literals and param names look like. A pattern part
// that is anything else (a splat, an optional or unexpected form) fails closed.
const LITERAL = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const PARAM = /^:[A-Za-z][A-Za-z0-9]*$/;
// Both portals count as our own site, so moving between them isn't a referral.
const PORTAL_ORIGINS = new Set(['https://portal.travelminds.com', 'https://portal-test.travelminds.com']);
// The only query params sent, matched exactly (so UTM_SOURCE or utm_id is dropped).
const UTM_PARAMS = new Set(['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content']);
const UTM_MAX_LENGTH = 64;
// An email address, whitespace (a name, or '+' decoded) or a 6+ digit run (an id or phone number).
const UTM_PERSONAL = /[@\s]|\d{6}/;

interface RouteMatchLike {
  route: { path?: string };
}

interface RouterStateLike {
  matches: RouteMatchLike[];
  errors?: Record<string, unknown> | null;
}

/**
 * The matched route pattern, joined from each match's route.path. Built only
 * from our route config, never from the URL. Unmatched (the router reports a
 * 404 error), a splat, or anything unexpected gives UNMATCHED.
 */
export function routePattern(state: RouterStateLike | null | undefined): string {
  if (!state || !state.matches?.length || (state.errors && Object.keys(state.errors).length)) return UNMATCHED;
  const parts: string[] = [];
  for (const { route } of state.matches) {
    for (const part of (route.path ?? '').split('/')) {
      if (!part) continue;
      if (!LITERAL.test(part) && !PARAM.test(part)) return UNMATCHED;
      parts.push(part);
    }
  }
  return '/' + parts.join('/');
}

/**
 * Whether a query param may reach Google: one of UTM_PARAMS, its decoded value
 * at most UTM_MAX_LENGTH characters and free of anything UTM_PERSONAL matches.
 */
export function keepUtm(name: string, value: string): boolean {
  return UTM_PARAMS.has(name) && value.length <= UTM_MAX_LENGTH && !UTM_PERSONAL.test(value);
}

/** origin + route pattern (UNMATCHED when none is known) + the utm params keepUtm allows. */
export function scrubLocation(href: string, pattern: string = UNMATCHED): string {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return '';
  }
  const kept = new URLSearchParams();
  url.searchParams.forEach((value, name) => {
    if (keepUtm(name, value)) kept.append(name, value);
  });
  const query = kept.toString();
  return url.origin + pattern + (query ? `?${query}` : '');
}

export function scrubReferrer(referrer: string, ownOrigin: string): string {
  if (!referrer) return '';
  try {
    const { origin } = new URL(referrer);
    return origin === ownOrigin || PORTAL_ORIGINS.has(origin) || origin === 'null' ? '' : origin;
  } catch {
    return '';
  }
}

let gtag: Gtag | null = null;
let lastNavigation: string | null = null;
let lastUrl: string | null = null;
let identityKey: string | null = null;

// The parts of window the loader touches, so tests can pass a fake.
interface AnalyticsWindow {
  dataLayer?: unknown[];
  gtag?: Gtag;
  location: { href: string; hostname: string };
  document: {
    referrer: string;
    head: { appendChild: (node: never) => unknown };
    createElement: (tag: 'script') => { async: boolean; src: string };
  };
}

/** Load gtag.js once. Returns false (and does nothing) without a valid id. */
export function initAnalytics(
  measurementId: string | undefined,
  win: AnalyticsWindow = globalThis as unknown as AnalyticsWindow
): boolean {
  if (gtag) return true;
  if (!measurementId || !MEASUREMENT_ID.test(measurementId) || !win?.document) return false;
  const dataLayer = (win.dataLayer = win.dataLayer || []);
  gtag = win.gtag = function gtagShim() {
    // gtag.js expects the arguments object itself, not an array.
    // eslint-disable-next-line prefer-rest-params
    dataLayer.push(arguments);
  };
  gtag('js', new Date());
  // Before config, so nothing gtag sends on its own sees the raw URL. The router
  // isn't consulted yet, so the page is UNMATCHED until the first page_view.
  setPage(win.location.href, UNMATCHED, win.document.referrer);
  gtag('config', measurementId, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    cookie_domain: win.location.hostname,
  });
  const script = win.document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  win.document.head.appendChild(script as never);
  return true;
}

function setPage(href: string, pattern: string, referrer: string): void {
  const origin = (() => {
    try {
      return new URL(href).origin;
    } catch {
      return '';
    }
  })();
  gtag?.('set', {
    page_location: scrubLocation(href, pattern),
    page_referrer: scrubReferrer(referrer, origin),
    page_title: PAGE_TITLE,
  });
}

/**
 * A manual page_view for one committed navigation, identified by its router
 * location key, so a repeated notification (or a Strict Mode re-subscribe)
 * for the same navigation sends nothing. Every new navigation counts,
 * query-only and hash changes included, except one to the URL already shown
 * (a redirect effect re-navigating to its own page). `href` may carry the
 * fragment; only that comparison sees it.
 */
export function trackPageView(href: string, pattern: string, navigationKey: string, documentReferrer = ''): void {
  if (!gtag || navigationKey === lastNavigation) return;
  const first = lastNavigation === null;
  const sameUrl = href === lastUrl;
  lastNavigation = navigationKey;
  lastUrl = href;
  if (sameUrl) return;
  // After the first page the referrer is our own previous page, which scrubs to ''.
  setPage(href, pattern, first ? documentReferrer : '');
  gtag('event', 'page_view');
}

interface RouterLocation {
  pathname: string;
  search: string;
  hash: string;
  key: string;
}

interface RouterLike {
  state: RouterStateLike & { location: RouterLocation; navigation: { state: string } };
  subscribe: (listener: (state: RouterLike['state']) => void) => () => void;
}

/** Sends the current page, then one page_view per completed navigation. Returns the unsubscribe. */
export function trackRouter(router: RouterLike, origin: string, documentReferrer = ''): () => void {
  const send = (state: RouterLike['state']) => {
    const { location, navigation } = state;
    if (navigation.state !== 'idle') return;
    trackPageView(origin + location.pathname + location.search + location.hash, routePattern(state), location.key, documentReferrer);
  };
  send(router.state);
  return router.subscribe(send);
}

export function setIdentity(ga: GaIdentity | null): void {
  if (!gtag) return;
  if (!ga) {
    clearIdentity();
    return;
  }
  const key = `${ga.user_id}|${ga.user_role}|${ga.agency_hash ?? ''}`;
  if (key === identityKey) return;
  identityKey = key;
  gtag('set', { user_id: ga.user_id });
  gtag('set', 'user_properties', { user_role: ga.user_role, agency_hash: ga.agency_hash ?? null });
}

export function clearIdentity(): void {
  if (!gtag || identityKey === null) return;
  identityKey = null;
  gtag('set', { user_id: null });
  gtag('set', 'user_properties', { user_role: null, agency_hash: null });
}

/**
 * Keeps the GA identity in step with the signed-in user and selected agency.
 * Only the latest call's response is applied, so a slow answer for a previous
 * agency can't overwrite the current one.
 */
export function createIdentitySync(fetchIdentity: (clientId: number | null) => Promise<GaIdentity | null>) {
  let latest = 0;
  return async (userId: number | null, clientId: number | null): Promise<void> => {
    if (!gtag) return;
    const call = ++latest;
    if (userId === null) {
      clearIdentity();
      return;
    }
    let ga: GaIdentity | null = null;
    try {
      ga = await fetchIdentity(clientId);
    } catch {
      ga = null;
    }
    if (call === latest) setIdentity(ga);
  };
}
